import React, { useState, useEffect } from 'react';

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  formatter?: (value: number) => string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 2000,
  decimals = 0,
  prefix = '',
  suffix = '',
  className = '',
  formatter
}) => {
  const [currentValue, setCurrentValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (value === 0) {
      setCurrentValue(0);
      return;
    }

    setIsAnimating(true);
    const startTime = Date.now();
    const startValue = currentValue;
    const difference = value - startValue;

    const updateCounter = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const newValue = startValue + (difference * easeOut);
      
      setCurrentValue(newValue);
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        setCurrentValue(value);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(updateCounter);
  }, [value, duration]);

  const formatValue = (val: number) => {
    if (formatter) {
      return formatter(val);
    }
    
    const formattedValue = val.toFixed(decimals);
    return `${prefix}${formattedValue}${suffix}`;
  };

  return (
    <span className={`${className} ${isAnimating ? 'text-blue-600' : ''} transition-colors duration-300`}>
      {formatValue(currentValue)}
    </span>
  );
};

export default AnimatedCounter; 